import React, { useCallback, useEffect, useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen,
  Calendar,
  Clock,
  Power,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
  ChevronsDown,
  Save,
  Pencil,
  Store,
  GripVertical,
  Tag,
  Download,
  Play,
  Zap,
  Loader2,
  Search,
  X,
} from "lucide-react";
import ProviderChips from "../components/ProviderChips";

const DAYS = [
  { v: 0, label: "Lun" },
  { v: 1, label: "Mar" },
  { v: 2, label: "Mer" },
  { v: 3, label: "Gio" },
  { v: 4, label: "Ven" },
  { v: 5, label: "Sab" },
  { v: 6, label: "Dom" },
];

function MediaThumb({ url }) {
  const [open, setOpen] = useState(false);
  if (!url) {
    return (
      <div className="h-full w-full bg-[var(--mp-dash-bg)] grid place-items-center text-[10px] text-[var(--mp-muted)]">
        no media
      </div>
    );
  }
  const full = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={isVideo ? "Riproduci video" : "Apri immagine"}
        data-testid="rubrica-foto-media-thumb-btn"
        className="h-full w-full block relative group cursor-pointer"
      >
        {isVideo ? (
          <video
            src={full}
            className="h-full w-full object-cover pointer-events-none"
            muted
            preload="metadata"
          />
        ) : (
          <img
            src={full}
            alt=""
            className="h-full w-full object-cover pointer-events-none"
          />
        )}
        <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/30 transition-colors">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-[10px] font-bold tracking-wide drop-shadow">
            {isVideo ? "▶︎" : "🔍"}
          </span>
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="rubrica-foto-media-lightbox"
          className="max-w-3xl rounded-3xl p-3 sm:p-4"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-base tracking-tight">
              Anteprima
            </DialogTitle>
          </DialogHeader>
          <div className="grid place-items-center bg-black/5 rounded-2xl overflow-hidden">
            {isVideo ? (
              <video
                src={full}
                className="max-h-[70vh] w-auto"
                controls
                autoPlay
                playsInline
              />
            ) : (
              <img src={full} alt="anteprima" className="max-h-[70vh] w-auto" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function RubricaFoto() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openClientId, setOpenClientId] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [query, setQuery] = useState("");
  const [proOnly, setProOnly] = useState(false);

  // Filtered view — matches on client_name (only stable field available
  // in the /staff/rubrica-foto overview response).
  const filteredOverview = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = overview;
    if (proOnly) base = base.filter((c) => c.plan === "pro");
    if (!q) return base;
    return base.filter((c) =>
      (c.client_name || "").toLowerCase().includes(q),
    );
  }, [overview, query, proOnly]);

  const proCount = React.useMemo(
    () => overview.filter((c) => c.plan === "pro").length,
    [overview],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/rubrica-foto");
      setOverview(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runNow = async (force = false) => {
    if (force && !window.confirm(
      "Esegui rubrica ADESSO per tutti i clienti con rubrica attiva,\nignorando giorno e orario?\n\nVerrà pubblicato il primo della coda di ogni cliente che oggi non ha ancora pubblicato in rubrica."
    )) return;
    setRunning(true);
    try {
      const { data } = await api.post(
        `/admin/rubrica-foto/run-now${force ? "?force=true" : ""}`
      );
      const pub = data.published || 0;
      const skip = data.skipped || 0;
      if (pub === 0 && skip === 0) {
        toast.info(force ? "Nessun cliente con rubrica attiva" : "Nessun cliente da pubblicare in questo slot");
      } else {
        toast.success(
          `Rubrica eseguita · ${pub} pubblicat${pub === 1 ? "o" : "i"}, ${skip} saltat${skip === 1 ? "o" : "i"}`
        );
      }
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  const openDiagnose = async () => {
    setDiagOpen(true);
    setDiagLoading(true);
    try {
      const { data } = await api.get("/admin/rubrica-foto/diagnose");
      setDiagData(data);
    } catch (e) {
      toast.error(apiError(e));
      setDiagData(null);
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <div data-testid="rubrica-foto-page" className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Evergreen
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Rubrica Foto
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-2xl">
          Coda evergreen di foto per cliente. Ogni foto messa in rubrica entra qui in testa;
          il cron pubblica la prima della coda su Google Business Profile nei giorni e all'orario
          impostati, poi la sposta in fondo. Tieni la rubrica spenta finché non hai accumulato
          abbastanza foto.
        </p>
      </div>

      {isAdmin && (
        <div
          data-testid="rubrica-foto-admin-panel"
          className="rounded-3xl border-2 border-[var(--mp-ink)] bg-[var(--mp-yellow)]/30 p-4 flex flex-wrap items-center gap-3"
        >
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
              Strumenti admin
            </div>
            <div className="text-sm">
              Esegui manualmente il cron rubrica o ispeziona perché un cliente non è uscito.
            </div>
          </div>
          <Button
            variant="outline"
            onClick={openDiagnose}
            data-testid="rubrica-foto-diagnose-btn"
            className="rounded-full border-[var(--mp-ink)]"
          >
            <BookOpen className="h-4 w-4 mr-1" /> Diagnostica
          </Button>
          <Button
            onClick={() => runNow(false)}
            disabled={running}
            data-testid="rubrica-foto-run-now-btn"
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            {running ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-1" />
            )}
            Esegui ora (slot corrente)
          </Button>
          <Button
            onClick={() => runNow(true)}
            disabled={running}
            data-testid="rubrica-foto-force-now-btn"
            className="rounded-full bg-[var(--mp-primary)] hover:opacity-90 text-white"
          >
            {running ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Zap className="h-4 w-4 mr-1" />
            )}
            Forza pubblicazione
          </Button>
        </div>
      )}

      {loading && overview.length === 0 ? (
        <div className="text-center py-20 text-[var(--mp-muted)]">Caricamento…</div>
      ) : overview.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-12 text-center">
          <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <div className="font-display text-xl">Nessun cliente attivo</div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--mp-muted)] pointer-events-none" />
              <input
                type="search"
                data-testid="rubrica-foto-search-input"
                placeholder="Cerca cliente…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full h-11 pl-9 pr-9 rounded-full bg-white border border-[var(--mp-border)] text-sm focus:outline-none focus:border-[var(--mp-ink)] transition-colors"
              />
              {query && (
                <button
                  type="button"
                  data-testid="rubrica-foto-search-clear"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full grid place-items-center text-[var(--mp-muted)] hover:bg-[var(--mp-dash-bg)]"
                  aria-label="Pulisci ricerca"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              data-testid="rubrica-foto-filter-pro"
              onClick={() => setProOnly((v) => !v)}
              aria-pressed={proOnly}
              className={`h-11 px-4 rounded-full border text-xs font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                proOnly
                  ? "bg-[var(--mp-ink)] text-white border-[var(--mp-ink)]"
                  : "bg-white text-[var(--mp-ink)] border-[var(--mp-border)] hover:border-[var(--mp-ink)]"
              }`}
              title={proOnly ? "Mostra tutti i clienti" : "Mostra solo clienti Pro"}
            >
              Pro
              <span
                className={`ml-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${
                  proOnly ? "bg-white/20 text-white" : "bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]"
                }`}
              >
                {proCount}
              </span>
            </button>
          </div>
          {filteredOverview.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-8 text-center text-sm text-[var(--mp-muted)]">
              {proOnly && !query
                ? "Nessun cliente Pro"
                : proOnly
                  ? `Nessun cliente Pro corrisponde a "${query}"`
                  : `Nessun cliente corrisponde a "${query}"`}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredOverview.map((c) => (
                <ClientCard
                  key={c.client_id}
                  client={c}
                  onOpen={() => setOpenClientId(c.client_id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <ClientRubricaDialog
        clientId={openClientId}
        onClose={() => setOpenClientId(null)}
        onChanged={load}
      />

      <RubricaDiagnoseDialog
        open={diagOpen}
        loading={diagLoading}
        data={diagData}
        onClose={() => setDiagOpen(false)}
      />
    </div>
  );
}

function RubricaDiagnoseDialog({ open, loading, data, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="rubrica-foto-diagnose-dialog"
        className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Diagnostica rubrica
          </DialogTitle>
          <DialogDescription>
            Stato esatto visto dal cron orario. ✅ = il check matcha · ❌ = il check fallisce e il cron salta il cliente.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-10 text-[var(--mp-muted)]">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : !data ? null : (
          <div className="space-y-4">
            <div className="rounded-2xl bg-[var(--mp-dash-bg)] border border-[var(--mp-border)] p-3 text-xs">
              <strong>Adesso (Europe/Rome):</strong>{" "}
              {new Date(data.now_rome).toLocaleString("it-IT")} ·{" "}
              giorno {data.weekday_now} · ora {data.hour_now}:00
            </div>
            {data.clients.length === 0 ? (
              <div className="text-sm text-[var(--mp-muted)] italic">
                Nessun cliente ha la rubrica attiva.
              </div>
            ) : (
              data.clients.map((c) => (
                <div
                  key={c.client_id}
                  data-testid={`rubrica-diag-${c.client_id}`}
                  className={`rounded-2xl border-2 p-3 ${
                    c.would_fire_now
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-[var(--mp-border)] bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <strong className="font-display tracking-tight">
                      {c.client_name}
                    </strong>
                    <Badge
                      className={`rounded-full text-[10px] uppercase tracking-[0.18em] ${
                        c.would_fire_now
                          ? "bg-emerald-200 text-emerald-900 border border-emerald-400"
                          : "bg-stone-100 text-stone-700 border border-stone-300"
                      }`}
                    >
                      {c.would_fire_now ? "Uscirebbe ora" : "Non uscirebbe"}
                    </Badge>
                  </div>
                  <div className="text-xs grid grid-cols-2 gap-x-4 gap-y-1">
                    <div>
                      Status:{" "}
                      <code>{String(c.status ?? "—")}</code>{" "}
                      {c.checks.match_status_approved ? "✅" : "❌"}
                    </div>
                    <div>
                      Ora settata:{" "}
                      <code>{String(c.hour_int_normalized ?? "—")}</code>{" "}
                      {c.checks.match_hour ? "✅" : "❌"}
                    </div>
                    <div>
                      Giorni: <code>[{(c.days_int_normalized || []).join(",")}]</code>{" "}
                      {c.checks.match_weekday ? "✅" : "❌"}
                    </div>
                    <div>
                      Coda: <code>{c.queue_size}</code>{" "}
                      {c.checks.queue_has_head ? "✅" : "❌ vuota"}
                    </div>
                    <div className="col-span-2">
                      Già pubblicato oggi:{" "}
                      <code>{c.rubrica_last_auto_at || "—"}</code>{" "}
                      {c.checks.already_published_today ? "❌ già fatto" : "✅"}
                    </div>
                    <div className="col-span-2">
                      Brand Metricool:{" "}
                      <code>{c.metricool_blog_id ?? "non collegato"}</code>
                    </div>
                  </div>
                  {c.head_item && (
                    <div className="mt-2 text-[11px] text-[var(--mp-muted)] italic truncate">
                      Prossimo: {c.head_item.title || c.head_item.caption || c.head_item.file_name || "—"}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ClientCard({ client, onOpen }) {
  const enabled = client.settings?.enabled;
  const hour = client.settings?.hour ?? 11;
  const minute = client.settings?.minute ?? 0;
  const days = client.settings?.days || [];
  return (
    <button
      type="button"
      data-testid={`rubrica-client-card-${client.client_id}`}
      onClick={onOpen}
      className="text-left rounded-3xl border-2 border-[var(--mp-ink)] bg-white p-5 hover:bg-[var(--mp-dash-bg)] transition-colors"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-full bg-[var(--mp-yellow)] grid place-items-center border border-[var(--mp-ink)] shrink-0">
            <Store className="h-4 w-4" />
          </div>
          <div className="font-display text-lg tracking-tight truncate">
            {client.client_name}
          </div>
        </div>
        <Badge
          className={`rounded-full text-[10px] uppercase tracking-[0.18em] ${
            enabled
              ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
              : "bg-stone-100 text-stone-600 border border-stone-300"
          }`}
        >
          {enabled ? "ON" : "OFF"}
        </Badge>
      </div>
      <div className="flex items-center gap-2 text-xs text-[var(--mp-muted)] mb-2">
        <Clock className="h-3.5 w-3.5" />
        <span>
          {String(hour).padStart(2, "0")}:{String(minute).padStart(2, "0")}
        </span>
        <span>·</span>
        <span>
          {days.length === 0
            ? "Nessun giorno"
            : days.length === 7
            ? "Tutti i giorni"
            : days.map((d) => DAYS[d]?.label || "").join(", ")}
        </span>
      </div>
      <div className="text-sm">
        <strong>{client.queue_size}</strong> in coda
        {client.next_item && (
          <>
            {" · prossimo: "}
            <span className="italic">
              {(client.next_item.title || client.next_item.caption || "—").slice(0, 40)}
            </span>
          </>
        )}
      </div>
    </button>
  );
}

function ClientRubricaDialog({ clientId, onClose, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [search, setSearch] = useState("");

  const reload = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/staff/rubrica-foto/${clientId}`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [clientId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!clientId) return null;

  const settings = data?.settings || { enabled: false, hour: 11, minute: 0, days: [] };
  const items = data?.items || [];
  const q = search.trim().toLowerCase();
  const filteredItems = q
    ? items.filter((it) => {
        const hay = [
          it.title,
          it.caption,
          it.file_name,
          ...(it.hashtags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
    : items;

  const patchSettings = async (patch) => {
    setSaving(true);
    try {
      const { data: res } = await api.patch(
        `/staff/rubrica-foto/${clientId}/settings`,
        patch
      );
      setData((prev) => ({ ...prev, settings: res.settings }));
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (d) => {
    const current = new Set(settings.days || []);
    if (current.has(d)) current.delete(d);
    else current.add(d);
    patchSettings({ days: Array.from(current).sort() });
  };

  const moveItem = async (id, target) => {
    try {
      await api.post(`/staff/rubrica-foto/${clientId}/items/${id}/move`, { target });
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Rimuovere questo video dalla rubrica?")) return;
    try {
      await api.delete(`/staff/rubrica-foto/${clientId}/items/${id}`);
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runBackfill = async () => {
    if (
      !window.confirm(
        "Importare in rubrica tutti i post già pubblicati/programmati su Metricool?\n\n" +
          "I post più vecchi vanno in testa (escono per primi al cron). I post già presenti vengono saltati."
      )
    )
      return;
    setImporting(true);
    try {
      const { data } = await api.post(
        `/staff/rubrica-foto/${clientId}/backfill`
      );
      toast.success(
        `Importati ${data.added} · saltati ${data.skipped} · totale ${data.queue_size}`
      );
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setImporting(false);
    }
  };

  const publishNow = async () => {
    if (
      !window.confirm(
        "Pubblicare ADESSO la prima foto della coda di questo cliente su Google Business Profile?\n\n" +
          "Ignora giorno e orario impostati. La manda direttamente a Metricool (GMB) e " +
          "sposta l'item in fondo alla coda."
      )
    )
      return;
    setPublishing(true);
    try {
      const { data: res } = await api.post(
        `/admin/rubrica-foto/run-client/${clientId}`
      );
      if (res?.ok) {
        toast.success(
          `Pubblicata ✓ (Metricool ID: ${res.metricool_post_id || "—"})`
        );
      } else {
        toast.error(`Non pubblicata: ${res?.reason || res?.error || "errore sconosciuto"}`);
      }
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={!!clientId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="rubrica-foto-dialog"
        className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <BookOpen className="h-5 w-5" /> Rubrica · {data?.client_name || "—"}
          </DialogTitle>
          <DialogDescription>
            Coda evergreen del salone. La testa esce per prima al prossimo cron.
          </DialogDescription>
        </DialogHeader>

        {/* Settings */}
        <div className="rounded-2xl border-2 border-[var(--mp-ink)] bg-white p-4 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="rubrica-foto-toggle-enabled"
                onClick={() => patchSettings({ enabled: !settings.enabled })}
                disabled={saving}
                className={`h-10 px-4 rounded-full border-2 inline-flex items-center gap-2 text-sm font-medium transition-colors ${
                  settings.enabled
                    ? "bg-emerald-500 border-emerald-700 text-white"
                    : "bg-white border-[var(--mp-ink)] text-[var(--mp-ink)]"
                }`}
              >
                <Power className="h-4 w-4" />
                {settings.enabled ? "Rubrica ATTIVA" : "Rubrica spenta"}
              </button>
              <div className="text-xs text-[var(--mp-muted)] max-w-xs">
                {settings.enabled
                  ? "Il cron pubblica automaticamente nei giorni/orari impostati."
                  : "Quando spenta i video entrano in coda ma non vengono ripubblicati."}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-[260px_1fr] gap-3 items-start">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block flex items-center gap-1">
                <Clock className="h-3 w-3" /> Orario pubblicazione
              </label>
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  min={0}
                  max={23}
                  data-testid="rubrica-foto-hour"
                  value={settings.hour}
                  onChange={(e) =>
                    setData((prev) => ({
                      ...prev,
                      settings: { ...prev.settings, hour: Number(e.target.value) },
                    }))
                  }
                  onBlur={() => patchSettings({ hour: settings.hour })}
                  className="rounded-full border-[var(--mp-border)] h-10 w-20 text-center"
                />
                <span className="font-display text-xl text-[var(--mp-muted)]">:</span>
                <select
                  data-testid="rubrica-foto-minute"
                  value={settings.minute ?? 0}
                  onChange={(e) => {
                    const m = Number(e.target.value);
                    setData((prev) => ({
                      ...prev,
                      settings: { ...prev.settings, minute: m },
                    }));
                    patchSettings({ minute: m });
                  }}
                  className="rounded-full border border-[var(--mp-border)] bg-white h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--mp-yellow)]"
                >
                  {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                  ))}
                </select>
              </div>
              <div className="text-[11px] text-[var(--mp-muted)] mt-1">
                Europe/Rome · {String(settings.hour).padStart(2, "0")}:{String(settings.minute ?? 0).padStart(2, "0")}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Giorni della settimana
              </label>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d) => {
                  const active = (settings.days || []).includes(d.v);
                  return (
                    <button
                      key={d.v}
                      type="button"
                      data-testid={`rubrica-day-${d.v}`}
                      onClick={() => toggleDay(d.v)}
                      disabled={saving}
                      className={`h-9 px-3 rounded-full border text-xs font-medium transition-colors ${
                        active
                          ? "bg-[var(--mp-ink)] border-[var(--mp-ink)] text-white"
                          : "bg-white border-[var(--mp-border)] text-[var(--mp-muted)] hover:border-[var(--mp-ink)]"
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-[var(--mp-muted)] mt-1">
                Selezionati: <strong>{(settings.days || []).length}/7</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Queue */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
              Coda · {items.length} video{q && filteredItems.length !== items.length ? ` · ${filteredItems.length} risultati` : ""}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={runBackfill}
              disabled={importing}
              data-testid="rubrica-foto-backfill"
              className="rounded-full border-[var(--mp-ink)] h-8 text-xs"
              title="Importa nella rubrica i post già pubblicati su Metricool"
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              {importing ? "Importazione…" : "Importa storici"}
            </Button>
            {isAdmin && items.length > 0 && (
              <Button
                size="sm"
                onClick={publishNow}
                disabled={publishing}
                data-testid="rubrica-foto-publish-now-btn"
                className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white h-8 text-xs"
                title="Pubblica subito la prima foto della coda, ignorando giorno/orario"
              >
                {publishing ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5 mr-1" />
                )}
                {publishing ? "Pubblicazione…" : "Pubblica ora"}
              </Button>
            )}
          </div>
          {items.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--mp-muted)] pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per titolo, caption o hashtag…"
                data-testid="rubrica-foto-search-input"
                className="pl-9 rounded-full border-[var(--mp-border)] bg-[var(--mp-dash-bg)] h-9 text-sm"
              />
            </div>
          )}
          {items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-8 text-center text-sm text-[var(--mp-muted)]">
              La coda è vuota. Pubblica un video con il flag "Inserisci in rubrica" attivo.
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-8 text-center text-sm text-[var(--mp-muted)]">
              Nessun video corrisponde a "<strong>{search}</strong>".
            </div>
          ) : (
            filteredItems.map((item) => {
              const idx = items.indexOf(item);
              return (
              <div
                key={item.id}
                data-testid={`rubrica-item-${item.id}`}
                className="rounded-2xl border border-[var(--mp-border)] bg-white p-3 flex items-start gap-3"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--mp-muted)] w-8 shrink-0 grid place-items-center pt-3">
                  <GripVertical className="h-3 w-3" />
                  #{idx + 1}
                </div>
                <div className="h-16 w-16 rounded-xl bg-[var(--mp-dash-bg)] border border-[var(--mp-border)] overflow-hidden shrink-0">
                  <MediaThumb url={item.dropbox_path ? `/api/files/dropbox?path=${encodeURIComponent(item.dropbox_path)}` : ""} />
                </div>
                <div className="flex-1 min-w-0">
                  {item.title && (
                    <div className="font-display text-sm tracking-tight truncate">
                      {item.title}
                    </div>
                  )}
                  <div className="text-xs whitespace-pre-wrap line-clamp-2 text-[var(--mp-ink)]">
                    {item.caption || <em className="text-[var(--mp-muted)]">nessuna caption</em>}
                  </div>
                  {item.hashtags?.length > 0 && (
                    <div className="text-[10px] text-[var(--mp-muted)] truncate mt-0.5">
                      {item.hashtags.join(" ")}
                    </div>
                  )}
                  <div className="text-[10px] text-[var(--mp-muted)] mt-1">
                    {item.publish_count > 0
                      ? `Pubblicato ${item.publish_count}× · ultima ${new Date(item.last_published_at).toLocaleDateString("it-IT")}`
                      : "Mai ripubblicato"}
                  </div>
                  <div className="mt-1.5">
                    <ProviderChips
                      active={item.providers || ["gmb"]}
                      allowed={item.allowed_providers || ["gmb", "facebook", "instagram"]}
                      size="xs"
                      testidPrefix={`rubrica-foto-chips-${item.id}`}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => moveItem(item.id, "top")}
                    title="Sposta in testa"
                    data-testid={`rubrica-move-top-${item.id}`}
                    className="h-7 w-7 rounded-full border border-[var(--mp-border)] bg-white grid place-items-center hover:bg-[var(--mp-yellow)]"
                  >
                    <ChevronsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(item.id, "up")}
                    title="Sposta su di una posizione"
                    data-testid={`rubrica-move-up-${item.id}`}
                    className="h-7 w-7 rounded-full border border-[var(--mp-border)] bg-white grid place-items-center hover:bg-[var(--mp-yellow)]"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(item.id, "down")}
                    title="Sposta giù di una posizione"
                    data-testid={`rubrica-move-down-${item.id}`}
                    className="h-7 w-7 rounded-full border border-[var(--mp-border)] bg-white grid place-items-center hover:bg-[var(--mp-yellow)]"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(item.id, "bottom")}
                    title="Sposta in fondo"
                    data-testid={`rubrica-move-bottom-${item.id}`}
                    className="h-7 w-7 rounded-full border border-[var(--mp-border)] bg-white grid place-items-center hover:bg-[var(--mp-yellow)]"
                  >
                    <ChevronsDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingItem(item)}
                    title="Modifica titolo/caption/hashtag"
                    data-testid={`rubrica-edit-${item.id}`}
                    className="h-7 w-7 rounded-full border border-[var(--mp-border)] bg-white grid place-items-center hover:bg-[var(--mp-yellow)]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    title="Rimuovi"
                    data-testid={`rubrica-remove-${item.id}`}
                    className="h-7 w-7 rounded-full border border-[var(--mp-border)] bg-white grid place-items-center hover:bg-rose-50 text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              );
            })
          )}
        </div>

        <RubricaItemEditDialog
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null);
            reload();
          }}
          clientId={clientId}
        />
      </DialogContent>
    </Dialog>
  );
}

function RubricaItemEditDialog({ item, clientId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setTitle(item.title || "");
    setCaption(item.caption || "");
    setHashtags((item.hashtags || []).join(" "));
    setProviders(item.providers || ["gmb"]);
    // Only re-seed when a different rubrica item is opened in the dialog;
    // re-running on every render would discard user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  if (!item) return null;

  const allowed = item.allowed_providers || ["gmb", "facebook", "instagram"];

  const save = async () => {
    if (!providers || providers.length === 0) {
      toast.error("Seleziona almeno una piattaforma di pubblicazione");
      return;
    }
    setSaving(true);
    try {
      const tags = hashtags
        .split(/\s+/)
        .map((t) => t.trim())
        .filter(Boolean)
        .map((t) => (t.startsWith("#") ? t : "#" + t))
        .slice(0, 5);
      await api.patch(`/staff/rubrica-foto/${clientId}/items/${item.id}`, {
        title: title.trim(),
        caption,
        hashtags: tags,
        providers,
      });
      toast.success("Item aggiornato");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">
            Modifica item rubrica
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
              Titolo
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={60}
              className="rounded-full border-[var(--mp-border)] h-10"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
              Caption
            </label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={2000}
              className="min-h-[120px] rounded-2xl border-[var(--mp-border)]"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block flex items-center gap-1">
              <Tag className="h-3 w-3" /> Hashtag (max 5)
            </label>
            <Textarea
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              className="min-h-[60px] rounded-2xl border-[var(--mp-border)] font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-1.5 block">
              Pubblica su (clicca per attivare/spegnere)
            </label>
            <ProviderChips
              active={providers}
              allowed={allowed}
              onChange={setProviders}
              size="sm"
              testidPrefix="rubrica-foto-edit-chip"
            />
            <div className="text-[10px] text-[var(--mp-muted)] mt-1.5">
              Quando il cron rubrica esegue questo item, lo pubblicherà solo sulle piattaforme accese.
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--mp-border)]">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border-[var(--mp-ink)]"
          >
            Annulla
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
